import { useMemo, useState } from 'react'
import { useQuery, useZero } from '@rocicorp/zero/react'
import { ActivityFeed } from '../components/ActivityFeed.tsx'
import { Shell } from '../components/Shell.tsx'
import { formatCents } from '../lib/format.ts'
import { letterBucket, letterGroups, type LetterGroup } from '../lib/letter-groups.ts'
import { mutators } from '../zero/mutators.ts'
import { queries } from '../zero/queries.ts'
import { Require } from './guards.tsx'

/** Below this many people the whole roster fits on one screen — skip the groups. */
const GROUP_STEP_THRESHOLD = 16

interface OrderingPerson {
  id: string
  userId: string
  guestId: string | null
  displayName: string
  ownerName: string | null
  numberOnBack: number | null
}

function OrderInner() {
  const z = useZero()
  const [roster] = useQuery(queries.roster())
  const [orderingGuests] = useQuery(queries.orderingGuests())
  const [catalogue] = useQuery(queries.drinksCatalogue())
  const [selected, setSelected] = useState<OrderingPerson | null>(null)
  const [group, setGroup] = useState<LetterGroup | null>(null)
  const [pickingGroup, setPickingGroup] = useState(true)
  const [toast, setToast] = useState<string | null>(null)

  const people = useMemo<OrderingPerson[]>(() => {
    const membersById = new Map(roster.map((member) => [member.id, member]))
    return [
      ...roster.map((member) => ({
        id: `member:${member.id}`,
        userId: member.id,
        guestId: null,
        displayName: member.displayName,
        ownerName: null,
        numberOnBack: member.numberOnBack ?? null,
      })),
      ...orderingGuests.flatMap((guest) => {
        const owner = membersById.get(guest.userId)
        if (!owner) return []
        return [{
          id: `guest:${guest.id}`,
          userId: owner.id,
          guestId: guest.id,
          displayName: guest.name,
          ownerName: owner.displayName,
          numberOnBack: null,
        }]
      }),
    ].sort((a, b) => a.displayName.localeCompare(b.displayName))
  }, [orderingGuests, roster])

  const groups = useMemo(
    () =>
      people.length >= GROUP_STEP_THRESHOLD
        ? letterGroups(people.map((person) => person.displayName))
        : [],
    [people],
  )

  const visiblePeople = useMemo(() => {
    if (!group) return people
    const letters = new Set(group.letters)
    return people.filter((person) => letters.has(letterBucket(person.displayName)))
  }, [group, people])

  function showToast(msg: string) {
    setToast(msg)
    window.setTimeout(() => setToast((t) => (t === msg ? null : t)), 1600)
  }

  async function order(drinkId: string, description: string) {
    if (!selected) return
    try {
      await z.mutate(
        mutators.drinks.order({
          id: crypto.randomUUID(),
          userId: selected.userId,
          guestId: selected.guestId,
          drinkId,
        }),
      )
      showToast(`${description} → ${selected.displayName}`)
      // A finished order sends the next person back to the top of the flow.
      setSelected(null)
      setGroup(null)
      setPickingGroup(true)
    } catch {
      showToast('Order failed')
    }
  }

  /** Back out of the drinks list, keeping the group the person was found in. */
  function backToPeople() {
    setSelected(null)
  }

  function backToGroups() {
    setGroup(null)
    setPickingGroup(true)
  }

  function personButton(person: OrderingPerson) {
    const sub = person.ownerName
      ? `Guest of ${person.ownerName}`
      : person.numberOnBack != null
        ? `#${person.numberOnBack}`
        : null
    return (
      <button key={person.id} className="btn btn--split" onClick={() => setSelected(person)}>
        <span className="choice-main">
          <span>{person.displayName}</span>
          {sub ? <span className="choice-sub">{sub}</span> : null}
        </span>
      </button>
    )
  }

  // Step 1 of the flow: narrowing a long roster to an alphabetical group.
  const showGroups = !selected && pickingGroup && groups.length > 0

  const title = selected
    ? `Order for ${selected.displayName}`
    : showGroups
      ? 'Order — pick a group'
      : group
        ? `Order — ${group.label}`
        : 'Order — pick a person'

  const onBack = selected
    ? backToPeople
    : !showGroups && groups.length > 0
      ? backToGroups
      : undefined

  // One Shell for all three steps, so the activity log below keeps its place in
  // the tree. Giving each step its own Shell would remount the log — and reset
  // it — on every button press.
  return (
    <Shell title={title} wide onBack={onBack}>
      {showGroups ? (
        <div className="choice-list">
          <button
            className="btn"
            onClick={() => {
              setGroup(null)
              setPickingGroup(false)
            }}
          >
            All members
          </button>
          {groups.map((g) => (
            <button
              key={g.label}
              className="btn"
              onClick={() => {
                setGroup(g)
                setPickingGroup(false)
              }}
            >
              {g.label}
            </button>
          ))}
        </div>
      ) : !selected ? (
        // Step 2 — pick a member or guest from the chosen group.
        <>
          {people.length === 0 ? (
            <p className="empty">No approved members yet. Seed the database.</p>
          ) : visiblePeople.length === 0 ? (
            <p className="empty">Nobody in this group.</p>
          ) : (
            <div className="choice-list">{visiblePeople.map(personButton)}</div>
          )}
          {groups.length > 0 ? (
            <button className="btn btn-secondary choice-back" onClick={backToGroups}>
              ← Back to groups
            </button>
          ) : null}
        </>
      ) : (
        // Step 3 — pick a drink for the selected member or guest.
        <>
          <div className="choice-list">
            {catalogue.map((d) => (
              <button
                key={d.drinkId}
                className="btn btn-secondary btn--split"
                onClick={() => order(d.drinkId, d.description)}
              >
                <span>{d.description}</span>
                <span className="choice-price">{formatCents(d.priceCents)}</span>
              </button>
            ))}
          </div>
          <button className="btn btn-secondary choice-back" onClick={backToPeople}>
            ← Back to people
          </button>
        </>
      )}

      <ActivityFeed />
      {toast ? <div className="toast">{toast}</div> : null}
    </Shell>
  )
}

export function OrderPage() {
  // Kiosk + admin can order for anyone; members use their own tab instead.
  return (
    <Require roles={['kiosk', 'admin']}>
      <OrderInner />
    </Require>
  )
}
